import {
  type LayoutType,
  type MaskOptions,
} from '@wandb/weave/common/types/media';
import memoize from 'memoize-one';

import {type RunSignature} from '../../types/run';
import {runHistoryRowsWithOneOfKeys} from '../../util/media';
import {
  type AllBoundingBoxControls,
  type AllMaskControls,
  type MediaPanelCardControl,
  type MoleculeConfig,
  type RunWithHistoryAndMediaWandb,
} from '../MediaCard';
import {
  type PointCloudRgb as RgbColor,
  type PointCloudRgba as RgbaColor,
} from '../PointCloudCard/types';

export const rowsWithKeysMemo = memoize(runHistoryRowsWithOneOfKeys);
export type Axis = 'step' | 'index' | 'run' | 'camera';
export type LayoutMode = 'gallery' | 'grid' | 'compare';

export type PointColorSetting = {
  type: 'rgb';
  color: RgbColor;
};

/** Default when a point has neither RGB nor a category (settings + parse). */
export const DEFAULT_POINT_COLOR: RgbColor = [230, 112, 23]; // burnt orange

/** Keys for Object3D bounding-box toggles / score sliders in panel config. */
export const BOXES = 'boxes';
export const GROUP_NAME_3D_BOUNDING_BOXES = 'Labels';
export const DEFAULT_SCORE_GROUP_NAME = 'score';

// Using google palette - consider making this dynamic
// http://google.github.io/palette.js/
export const colorMap: {[key: number]: RgbColor} = {
  0: [21, 128, 114], // teal-ish?
  1: [55, 126, 184], // blue
  2: [102, 166, 30], // green
  3: [12, 216, 237], // bright darker blue
  4: [152, 78, 162], // purple
  5: [0, 210, 213], // brighter light blue-ish
  6: [255, 127, 0], // orange
  7: [175, 141, 0], // yucky brown-ish yellow
  8: [127, 128, 205], // blue-ish purple
  9: [179, 233, 0], // neon-ish green
  10: [196, 46, 96], // red-ish pink-ish
  11: [166, 86, 40], // brown
  12: [247, 129, 191], // lighter pink
  13: [144, 211, 199], // light green-ish blue-ish
  14: [190, 186, 218], // purple-ish grey
  15: [251, 128, 114], // pink ish orange
};

/**
 * Resolve draw color for a point. Shared by Babylon and Potree so category
 * and default colors stay consistent across renderers.
 */
export function resolvePointColor(point: {
  color?: RgbColor;
  category?: number;
}): RgbColor {
  if (point.color) {
    return point.color;
  }
  if (point.category != null && colorMap[point.category] != null) {
    return colorMap[point.category];
  }
  return DEFAULT_POINT_COLOR;
}

export type KeyTypes = Partial<Record<string, string>>;

export type MediaSyncState = {
  currentTime: number;
  isPlaying: boolean;
  playbackRate: number;
  leaderId: string | null;
  lastUpdate: number;
};

export type ColumnConfig = {
  run?: string;
  step?: number;
  index?: number;
  mediaKey?: string;
};

export type TileControlType = 'run' | 'step' | 'index' | 'mediaKey';
export type TileControlValue = 'linked' | 'individual' | 'hidden';
export type TileControls = Partial<Record<TileControlType, TileControlValue>>;
export type FanOutVariable = 'step' | 'index' | 'run';

export type CompareSettings = {
  columns?: ColumnConfig[];
  runName?: string;
  tileControls?: TileControls;
  fanOutVariable?: FanOutVariable;
};

export type GallerySettings = {
  axis: Axis;
};

export type GridSettings = {
  xAxis: Axis;
  yAxis: Axis;
};

export type MediaBrowserPanelConfig = {
  chartTitle?: string; //
  stepIndex?: number; // index of the step shown from each run, default: 0
  mediaIndex?: number; // index of the image shown from each run, default: 0
  mediaSliderKey?: string; // key for slider values, defaults to '_step'
  mediaKeys?: string[]; // key for media, like 'examples'

  actualSize?: boolean; //
  fitToDimension?: boolean; //
  videoActualSize?: boolean; // Show videos at their original size (gallery mode); actualSize/fitToDimension are image-only
  pixelated?: boolean; //
  alwaysOriginal?: boolean; // Show original image (no thumbnails) in non-gallery modes

  // Settings for tiling
  columnCount?: number; //

  // Layout mode
  mode?: LayoutMode;

  // mode-specific settings
  gallerySettings?: GallerySettings;
  gridSettings?: GridSettings;
  compareSettings?: CompareSettings;

  // min/max values for the axis range selection
  selection?: {
    xAxis?: [number, number];
    yAxis?: [number, number];
  };

  page?: {
    start?: number;
  };

  // How to stack masks
  tileLayout?: {
    [mediaKey: string]: {
      type: LayoutType;
      maskOptions: MaskOptions[];
    };
  };

  // The number of steps to increase on the up and down buttons of the step slider
  stepStrideLength?: number;
  snapToExistingStep?: boolean;
  // `null` means "explicitly show all" — it blocks an inherited limit in the
  // workspace → section → panel cascade, unlike `undefined` which inherits.
  maxGalleryItems?: number | null;
  maxRuns?: number;
  maxYAxisCount?: number;
  moleculeConfig?: MoleculeConfig;
  segmentationMaskConfig?: AllMaskControls;
  boundingBoxConfig?: AllBoundingBoxControls;
  sliderSync?: boolean;

  // Video synchronization settings
  videoPlaybackSyncEnabled?: boolean; // if sync is on at _any_ settings level
  videoPlaybackSync?: boolean; // if sync is on at _this_ settings level
  videoPlaybackSyncAutoplay?: boolean;
  videoPlaybackSyncLoop?: boolean;
  // seek synchronized videos to this time on first load
  videoPlaybackSyncCurrentTime?: number;

  disableVideoStream?: boolean; // Opt out of video streaming

  // Object3D settings
  object3DBackgroundColor?: RgbaColor;
  pointColor?: PointColorSetting;
  useRightHandedSystem?: boolean;

  // How many seconds to seek forward/back when using the arrow keys
  videoPlayerTimeStep?: number;
};

export type NonEmptyTile = {
  mediaKey: string;
  mediaSliderKey?: string;
  mediaSliderKeyValue?: number;
  mediaStep: number;
  mediaIndex: number;
  runSignature: RunSignature;
  run: RunWithHistoryAndMediaWandb;
  controls?: MediaPanelCardControl;
  meta?: {
    camera?: {
      cameraIndex: number;
    };
  };

  // We don't set this for regular tiles, but it make the typing cleaner
  // to interact with empty tiles to have an optional here
  isEmpty?: false;
};
